"use client";

import { createContext, useContext, type ReactNode } from "react";

/** How PanZoom applies zoom/pan to content. */
export type PanZoomTransformMode = "css" | "camera";

export interface PanZoomScaleContextValue {
	/** User zoom factor (1 = fit / full-bleed for iso). */
	zoom: number;
	/**
	 * Virtual scale (baseScale × zoom) — used for pan bounds / content math.
	 * With `transformMode="camera"`, CSS transform stays identity.
	 */
	scale: number;
	pan: { x: number; y: number };
	viewportWidth: number;
	viewportHeight: number;
	/**
	 * `css` — CSS translate+scale (2D plate).
	 * `camera` — gestures only; WebGL dollies/pans (iso world).
	 */
	transformMode: PanZoomTransformMode;
	/**
	 * True while toolbar/reset (non-rapid) transform should ease.
	 * Camera-mode consumers SmoothDamp/lerp toward pan/zoom; CSS mode uses CSS transition.
	 */
	animateTransform: boolean;
	/** Duration matching CSS cubic-bezier window (ms). */
	transitionDurationMs: number;
}

const PanZoomScaleContext = createContext<PanZoomScaleContextValue>({
	zoom: 1,
	scale: 1,
	pan: { x: 0, y: 0 },
	viewportWidth: 0,
	viewportHeight: 0,
	transformMode: "css",
	animateTransform: false,
	transitionDurationMs: 200,
});

export function PanZoomScaleProvider({
	zoom,
	scale,
	pan,
	viewportWidth,
	viewportHeight,
	transformMode,
	animateTransform,
	transitionDurationMs,
	children,
}: PanZoomScaleContextValue & { children: ReactNode }) {
	return (
		<PanZoomScaleContext.Provider
			value={{
				zoom,
				scale,
				pan,
				viewportWidth,
				viewportHeight,
				transformMode,
				animateTransform,
				transitionDurationMs,
			}}
		>
			{children}
		</PanZoomScaleContext.Provider>
	);
}

/** Read pan-zoom scale / camera transform from an ancestor {@link PanZoomSurface}. */
export function usePanZoomScale(): PanZoomScaleContextValue {
	return useContext(PanZoomScaleContext);
}
